/**
 * Default Node Renderer
 *
 * Fallback renderer for unknown node types.
 * Provides basic LOD rendering: full → title → icon → dot
 * Phase 0.7: Supports WebGL texture caching for distant nodes
 */

import type { UINode, ScreenMetrics, WorldRect } from '../types';
import type { NodeRenderer, RenderLayers, RenderResult, LODLevel } from '../renderer';
import type { MeasureOptions } from '../content-measure';
import { calculateLOD } from '../renderer';
import { hashContent } from '../texture-cache';

/** Default texture threshold in screen pixels */
const DEFAULT_TEXTURE_THRESHOLD = 60;

/**
 * CSS classes for LOD levels
 */
const LOD_CLASSES = {
	full: 'lod-full',
	title: 'lod-title',
	icon: 'lod-icon',
	dot: 'lod-dot'
} as const;

/**
 * Default renderer with basic LOD support.
 */
export class DefaultRenderer implements NodeRenderer {
	/** Track last LOD per node to detect changes */
	private lastLOD: Map<string, LODLevel> = new Map();

	/** Track last content per node to detect changes */
	private lastContent: Map<string, string> = new Map();

	/** Track content hashes for texture cache invalidation */
	private contentHashes: Map<string, string> = new Map();

	render(node: UINode, metrics: ScreenMetrics, layers: RenderLayers): RenderResult {
		const screenSize = Math.max(metrics.width, metrics.height);
		const opts = layers.options;

		// 1. Calculate LOD first - this is independent of texture state
		// LOD is purely based on screen metrics and enabled LOD levels
		// forceLod takes precedence over everything
		const lod: LODLevel = opts.forceLod
			? opts.forceLod
			: opts.simplifyLOD
				? calculateLOD(metrics, {
						enableLodFull: opts.enableLodFull,
						enableLodTitle: opts.enableLodTitle,
						enableLodIcon: opts.enableLodIcon,
						enableLodDot: opts.enableLodDot
					})
				: 'full';

		// 2. Calculate content hash for texture cache invalidation
		// Include LOD level in the hash so different LODs get different textures
		const baseContentHash = this.getContentHash(node);
		const contentHash = `${baseContentHash}:${lod}`;

		// 3. Compound texture ID includes LOD level
		// Each LOD level gets its own cached texture
		const textureId = `${node.id}:${lod}`;

		// 4. Determine if we should use texture rendering
		// forceTextureRendering forces texture mode regardless of screen size
		const textureThreshold = opts.textureThreshold ?? DEFAULT_TEXTURE_THRESHOLD;
		const wantTexture = (opts.forceTextureRendering || screenSize < textureThreshold) && layers.webgl !== null;

		// 5. Check if we have a valid cached texture for this LOD level
		const hasValidTexture = wantTexture && layers.webgl?.hasValidTexture(textureId, contentHash);

		const lastLod = this.lastLOD.get(node.id);
		const lodChanged = lastLod !== lod;

		// Check if this is a newly created element (e.g., re-entered frustum)
		const isNewElement = layers.html.isNewlyCreated(node.id);

		// Update LOD class
		if (lodChanged && lastLod) {
			layers.html.removeClass(node.id, LOD_CLASSES[lastLod]);
		}
		layers.html.addClass(node.id, LOD_CLASSES[lod]);

		// Set data attribute for CSS styling
		layers.html.setDataAttribute(node.id, 'lod', lod);

		// Try texture rendering if we have a valid texture for this LOD level
		if (hasValidTexture && layers.webgl) {
			const worldRect: WorldRect = {
				x: node.position.x,
				y: node.position.y,
				width: node.size.width,
				height: node.size.height
			};

			// Rotation is applied via shader (texture is captured without rotation for efficiency)
			const textureDrawn = layers.webgl.drawCachedTexture(textureId, worldRect, 1, node.rotation ?? 0);

			if (textureDrawn) {
				// Successfully drew texture - hide HTML content
				layers.html.setContent(node.id, '');
				layers.html.addClass(node.id, 'texture-cached');

				// Clear lastContent so content is re-rendered when switching back to HTML
				this.lastContent.delete(node.id);

				this.lastLOD.set(node.id, lod);
				return { lod, changed: lodChanged };
			}
		}

		// Fall back to HTML rendering
		layers.html.removeClass(node.id, 'texture-cached');

		// Render content based on LOD (this happens FIRST, before texture capture)
		const content = this.getContent(node, lod);
		const lastContentValue = this.lastContent.get(node.id);
		const contentChanged = lastContentValue !== content || isNewElement;

		if (contentChanged) {
			layers.html.setContent(node.id, content);
			this.lastContent.set(node.id, content);
		}

		// Request texture generation AFTER HTML content is rendered
		// The texture will capture the current LOD content (not forced 'full')
		// Pass expectedLOD to guard against race condition where LOD changes during async capture
		if (wantTexture && layers.webgl && !hasValidTexture) {
			layers.webgl.requestTexture(
				textureId,
				() => layers.html.getContainer(node.id),
				contentHash,
				1,
				lod // expectedLOD for race condition validation
			);
		}

		this.lastLOD.set(node.id, lod);

		return { lod, changed: lodChanged || contentChanged };
	}

	/**
	 * Get a content hash for cache invalidation.
	 */
	private getContentHash(node: UINode): string {
		const cached = this.contentHashes.get(node.id);
		if (cached) {
			return cached;
		}

		const contentStr = JSON.stringify(node.data);
		const hash = hashContent(contentStr);
		this.contentHashes.set(node.id, hash);
		return hash;
	}

	/**
	 * Get content string based on LOD level.
	 */
	protected getContent(node: UINode, lod: LODLevel): string {
		const label = this.getLabel(node);
		const type = node.type;

		switch (lod) {
			case 'full':
				return `<div class="node-content">
					<div class="node-close-btn" data-action="close">&times;</div>
					<div class="node-type">${type}</div>
					<div class="node-label">${label}</div>
				</div>`;

			case 'title':
				return `<div class="node-title">${label}</div>`;

			case 'icon':
				return `<div class="node-icon">${this.getIcon(node)}</div>`;

			case 'dot':
				return `<div class="node-dot"></div>`;
		}
	}

	/**
	 * Get display label for a node.
	 */
	protected getLabel(node: UINode): string {
		return (node.data.label as string) || (node.data.title as string) || node.type;
	}

	/**
	 * Get icon representation for a node.
	 */
	protected getIcon(node: UINode): string {
		// Simple text-based icon fallback
		const label = this.getLabel(node);
		return label.slice(0, 2).toUpperCase();
	}

	getContentHTML(node: UINode): string {
		return this.getContent(node, 'full');
	}

	getMeasureOptions(_node: UINode): MeasureOptions {
		return { maxWidth: 400, minWidth: 100, minHeight: 40 };
	}

	getPreferredSize(node: UINode): { width: number; height: number } {
		return {
			width: node.size?.width ?? 150,
			height: node.size?.height ?? 100
		};
	}

	cleanup(nodeId: string): void {
		this.lastLOD.delete(nodeId);
		this.lastContent.delete(nodeId);
		this.contentHashes.delete(nodeId);
	}
}
